import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxImageCompressService } from 'ngx-image-compress';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { showNotification } from 'src/app/helpers/show-toast';
import { LatestOffer } from 'src/app/_models/latestOffers.model';
import { MobileRoutes } from 'src/app/_models/routes.model';
import { LatestOffersService } from 'src/app/_services/latest-offers.service';
import { RoutesService } from 'src/app/_services/routes.service';
import { baseUrl, environment } from 'src/environments/environment';

@Component({
  selector: 'app-add-latest-offer',
  templateUrl: './add-latest-offer.component.html',
  styleUrls: ['./add-latest-offer.component.scss'],
})
export class AddLatestOfferComponent implements OnInit {
  pageTitle: string = 'Latest Offers';
  routes: MobileRoutes[];
  ranks = Array(15);
  imageFile;
  editMode = false;
  specificRoute = false;
  idOfRoute;
  latestOffer: LatestOffer = {};
  addOfferForm: FormGroup;
  isSubmited: boolean = false;
  offerFileName: string = '';
  imageLimitSize: number = 2097152;
  compressedImageLimitSize: number = 102400;

  constructor(
    private latestOffersService: LatestOffersService,
    private routesService: RoutesService,
    private activatedRoute: ActivatedRoute,
    public toastr: ToastrService,
    private router: Router,
    private spinner: NgxSpinnerService,
    private _formBuilder: FormBuilder,
    private imageCompress: NgxImageCompressService
  ) {}

  ngOnInit(): void {
    this.addOfferForm = this._formBuilder.group({
      offerName: [
        null,
        [
          Validators.required,
          Validators.pattern(
            /^(?:[a-zA-Z0-9\s\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDCF\uFDF0-\uFDFF\uFE70-\uFEFF]|(?:\uD802[\uDE60-\uDE9F]|\uD83B[\uDE00-\uDEFF])){0,}$/
          ),
        ],
      ],
      offerRoute: [null, [Validators.required]],
      offerSpeceficRoute: [null],
      offerDuration: [null, [Validators.required]],
      offerRank: [null, [Validators.required]],
      offerFileInput: [null, [Validators.required]],
    });

    this.editMode =
      this.activatedRoute.snapshot.url[1] &&
      this.activatedRoute.snapshot.url[1].path === 'edit' &&
      true;

    this.getAllRoutes();

    if (this.editMode) {
      const id = this.activatedRoute.snapshot.params.offerId;
      this.getOfferById(id);
    }
  }

  getAllRoutes() {
    this.routesService.getAllRoutes().subscribe((res) => {
      this.routes = res.data?.routes;
    });
  }

  getOfferById(offerId) {
    this.latestOffersService.getOfferById(offerId).subscribe((res) => {
      this.latestOffer = res.data;

      this.offerFileName = res.data.picture
        ? res.data.picture.split('/').pop()
        : '';

      let startDate = new Date(this.latestOffer.startDate);
      let endDate = new Date(this.latestOffer.endDate);

      this.addOfferForm.patchValue({
        offerName: this.latestOffer.name,
        offerRoute: this.latestOffer.routeId,
        offerDuration: [startDate, endDate],
        offerRank: this.latestOffer.rank,
        offerFileInput: this.offerFileName,
      });

      document.getElementById(
        'placeholder'
      ).style.backgroundImage = `url(${this.latestOffer['picture']})`;

      setTimeout(() => {
        const route = this.routes.find((r) => r.routeId === +res.data.routeId);
        this.addOfferForm.controls['offerRoute'].setValue(route && route.id);
      }, 200);
    });
  }

  routeChanged(): void {
    const route = this.routes.find(
      (r) => r.id === +this.addOfferForm.value.offerRoute
    );

    if (route && !route.isHaveSpecialId) {
      this.latestOffer.routeName = route.routeName;
      this.latestOffer.routeId = route.routeId;
      this.specificRoute = false;
    } else {
      this.specificRoute = true;
    }
  }

  onImageUpload(event): void {
    event.preventDefault();

    this.imageCompress.uploadFile().then(({ image, orientation }) => {
      let imageSize = this.imageCompress.byteCount(image);

      if (imageSize > this.imageLimitSize) {
        showNotification(
          'danger',
          'You must upload an image with size less than 2 MegaByte!',
          this.toastr
        );
      } else {
        let newQuality =
          imageSize > this.compressedImageLimitSize
            ? Math.floor(100 / (imageSize / this.compressedImageLimitSize))
            : 100;

        let newRatio = newQuality;

        this.imageCompress
          .compressFile(image, orientation, newRatio, newQuality)
          .then((result: any) => {

            this.imageFile = this.dataURLtoFile(result, 'filename.png');

            document.getElementById(
              'placeholder'
            ).style.backgroundImage = `url(${result})`;

            this.addOfferForm.controls['offerFileInput'].setValue(
              event ? 'image.png' : ''
            );
          });
      }
    });
  }

  dataURLtoFile(dataurl, filename) {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  }

  generateLatestOfferObject(data) {
    this.latestOffer.name = data.offerName;
    this.latestOffer.startDate = data.offerDuration[0];
    this.latestOffer.endDate = data.offerDuration[1];
    this.latestOffer.rank = data.offerRank;
    this.latestOffer.routeId = +data.offerRoute;
  }

  onAddOfferSubmit() {
    this.isSubmited = true;

    this.generateLatestOfferObject(this.addOfferForm.value);

    if (this.addOfferForm.valid) {
      if (this.editMode) {
        this.latestOffersService
          .updateOffer(this.imageFile, this.latestOffer)
          .subscribe((res) => {
            showNotification(
              'success',
              `${res.data.name} has been updated`,
              this.toastr
            );
            this.router.navigateByUrl('offers');
          });
      } else {
        this.latestOffersService
          .addNewOffer(this.imageFile, this.latestOffer)
          .subscribe((res) => {
            showNotification(
              'success',
              `${res.data.name} has been added`,
              this.toastr
            );
            this.router.navigateByUrl('offers');
          });
      }
    } else {
      showNotification(
        'danger',
        'You should fill the form fields with the correct data!',
        this.toastr
      );
    }
  }
}
